import { memo, useCallback, useRef, useState } from "react";

import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Tooltip from "ui/components/Tooltip/Tooltip";
import { getUserAPI } from "services/api/users/api";
import { User } from "services/api/users/types";

export interface UserCellProps {
  fullName?: string | null;
  userId?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const getInitials = (name: string): string =>
  name
    .split(" ")
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

const TooltipRow: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({
  label,
  value,
  accent,
}) => (
  <div className="flex justify-between text-sm mb-1 gap-4">
    <span className="text-slate-300">{label}</span>
    <span className={`font-medium text-right max-w-[160px] truncate ${accent ?? ""}`}>{value}</span>
  </div>
);

/**
 * Prikaz korisnika u tablici — avatar (inicijali/slika) + ime.
 * Na hover dohvaća detalje (getUserAPI) i prikazuje dark tooltip
 * (email, telefon, rola), po uzoru na ContactCell / VehicleCell.
 */
const UserCell: React.FC<UserCellProps> = ({ fullName, userId, email, imageUrl, onClick }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedRef = useRef(false);

  const handleHover = useCallback(() => {
    if (fetchedRef.current || !userId) return;
    fetchedRef.current = true;
    setIsLoading(true);
    getUserAPI(userId)
      .then(setUser)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [userId]);

  if (!fullName && !user) return <span className="text-slate-400">—</span>;

  const displayName =
    fullName ?? [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const resolvedEmail = email ?? user?.email ?? null;
  const resolvedImage = imageUrl ?? user?.avatar ?? null;
  const role = user?.roles?.[0]?.name ?? null;
  const hasTooltip = !!userId || !!resolvedEmail;

  // Ikona ostaje izvan Tooltip-a; samo ime ide u `decorate` da isprekidana
  // linija (border-b border-dashed) bude samo pod tekstom — kao VehicleCell.
  return (
    <div className="flex items-center gap-2 min-w-0" onMouseEnter={handleHover} onClick={onClick}>
      <TableIcon initials={getInitials(displayName)} imageUrl={resolvedImage ?? undefined} size="small" />
      {hasTooltip ? (
        <div className="min-w-0">
          <Tooltip
            decorate={
              <span className="text-sm font-medium text-sky-600 hover:text-sky-900 truncate">
                {displayName}
              </span>
            }
            position="bottom-left"
            size="medium"
          >
            <div className="min-w-[220px]">
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Korisnik</div>
              <TooltipRow label="Ime" value={displayName} />
              {resolvedEmail && <TooltipRow label="Email" value={resolvedEmail} accent="text-sky-400" />}
              {user?.phoneNumber && <TooltipRow label="Telefon" value={user.phoneNumber} />}
              {role && <TooltipRow label="Rola" value={role} />}
              {isLoading && <div className="text-sm text-slate-400 mt-1">Učitavanje…</div>}
            </div>
          </Tooltip>
        </div>
      ) : (
        <span className="text-slate-700 truncate">{displayName}</span>
      )}
    </div>
  );
};

export default memo(UserCell);
